import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import { saveStoreEmailsConfigsDataApi } from 'api';
import { Formik } from 'formik';
import * as yup from 'yup';


const StoreConfigurationsReceivingEmails = () => {
  const dispatch = useDispatch();

  const { profile = {} } = useSelector((state: IReduxState) => state.user);
  const { storeEmailsData } = useSelector((state: IReduxState) => state.store_configs);

  const saleEmail = storeEmailsData.find((item) => item.path == 'sales_email/order/copy_to')?.value;
  const subscriptionsEmail = storeEmailsData.find((item) => item.path == 'subscriptions/billing_failed/copy_to')?.value;
  const requestAccessEmail = storeEmailsData.find((item) => item.path == 'request_access/general/send_to')?.value;
  const appointmentEmail = storeEmailsData.find((item) => item.path == 'webforms/email/email')?.value;
  const onHoldEmail = storeEmailsData.find((item) => item.path == 'dermpro_maxmind_config/general/email_address')?.value;

  const regexOneEmailAllowed = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,4})+$/;
  const regexSeveralEmailsAllowed = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4},?)+$/;

  const isPremiumStore = profile?.stores?.[0].storeType == 'premium' || profile?.stores?.[0].storeType == 'premium_plus';

  const handleUpdateEmails = (emailsType, emailField, resetAppointmentBlockEmail = false) => {
    let configs;
    const emailsToUpdate = storeEmailsData.filter((item) => item.path.includes(emailsType));

    if (emailsType == 'request_access/') {
      // scope: 'default', we need to use this param for (request_access/general/send_to),
      // because in magento it is available only on Default store view
      configs = emailsToUpdate.map((item) => {
        return {...item, value: emailField, scope: 'default'};
      });
    } else {
      configs = emailsToUpdate.map((item) => {
        return {...item, value: emailField};
      });
    }

    saveStoreEmailsConfigsDataApi({dispatch, configs, resetAppointmentBlockEmail});
  };

  return (
    <div className="m-5">
      <Formik
        initialValues={{
          saleEmail,
          subscriptionsEmail,
          requestAccessEmail,
          appointmentEmail,
          onHoldEmail,
        }}
        validationSchema={
          yup.object({
            saleEmail: yup
              .string()
              .matches(regexSeveralEmailsAllowed, 'Please enter a valid email address')
              .required('Field is required'),
            subscriptionsEmail: yup
              .string()
              .matches(regexSeveralEmailsAllowed, 'Please enter a valid email address')
              .required('Field is required'),
            requestAccessEmail: yup
              .string()
              .matches(regexSeveralEmailsAllowed, 'Please enter a valid email address')
              .required('Field is required'),
            appointmentEmail: yup
              .string()
              .matches(regexSeveralEmailsAllowed, 'Please enter a valid email address')
              .required('Field is required'),
            onHoldEmail: yup
              .string()
              .matches(regexOneEmailAllowed, 'Please enter a valid email address')
              .required('Field is required'),
          })}
        onSubmit={handleUpdateEmails}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange
        }) => (
          <form noValidate autoComplete="off" className="p-8">
            <Typography variant="h6" color="primary">
              Sales Emails
            </Typography>
            <div className="mb-3">
              Sales Order Confirmation Emails, Shipping Emails, Credit Memos, Order Updates, and Invoices that can be sent
              to the purchaser for any given purchase will be Blind Copied (BCC) to the Receiving Email Address(es).
            </div>
            <Grid item xs={12} className="flex mt-3 mb-6">
              <Grid item xs={7}>
                <TextField
                  value={values.saleEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  className="pr-5"
                  name="saleEmail"
                  label="staffmember1@yourdomain.com,staffmember2@yourdomain.com"
                  variant="outlined"
                  InputLabelProps={{ style: { fontStyle: 'italic' } }}
                  error={touched.saleEmail && Boolean(errors.saleEmail)}
                  helperText={touched.saleEmail && errors.saleEmail}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={values.saleEmail === saleEmail || Boolean(errors.saleEmail)}
                  onClick={() => {
                    handleUpdateEmails('sales_email/', values.saleEmail);
                  }}
                >
                  Update
                </Button>
              </Grid>
            </Grid>

            <Typography variant="h6" color="primary">
              Subscription Emails
            </Typography>
            <div className="mb-3">
              Emails associated with subscriptions, most commonly the Payment Failed
              Notification emails, will be Blind Copied (BCC) to the Receiving Email Address(es).
            </div>
            <Grid item xs={12} className="flex mt-3 mb-6">
              <Grid item xs={7}>
                <TextField
                  value={values.subscriptionsEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  className="pr-5"
                  name="subscriptionsEmail"
                  label="staffmember1@yourdomain.com,staffmember2@yourdomain.com"
                  variant="outlined"
                  InputLabelProps={{ style: { fontStyle: 'italic' } }}
                  error={touched.subscriptionsEmail && Boolean(errors.subscriptionsEmail)}
                  helperText={touched.subscriptionsEmail && errors.subscriptionsEmail}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={values.subscriptionsEmail === subscriptionsEmail || Boolean(errors.subscriptionsEmail)}
                  onClick={() => {
                    handleUpdateEmails('subscriptions/', values.subscriptionsEmail);
                  }}
                >
                  Update
                </Button>
              </Grid>
            </Grid>

            {isPremiumStore &&
            <>
              <Typography variant="h6" color="primary">
                Appointment Form Emails
              </Typography>
              <div className="mb-3">
                If you have an appointment request form on your DermPRO Store, we can configure form submittal
                notifications to be sent to any recipients you designate. We can send to one
                or multiple recipients.
              </div>
              <Grid item xs={12} className="flex mt-3 mb-6">
                <Grid item xs={7}>
                  <TextField
                    value={values.appointmentEmail}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    className="pr-5"
                    name="appointmentEmail"
                    label="appointmentsetter1@yourdomain.com,appointmentsetter2@yourdomain.com"
                    variant="outlined"
                    InputLabelProps={{ style: { fontStyle: 'italic' } }}
                    error={touched.appointmentEmail && Boolean(errors.appointmentEmail)}
                    helperText={touched.appointmentEmail && errors.appointmentEmail}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleUpdateEmails('webforms/', values.appointmentEmail, true);
                    }}
                    disabled={values.appointmentEmail === appointmentEmail || Boolean(errors.requestAccessEmail)}
                  >
                    Update
                  </Button>
                </Grid>
              </Grid>
            </>
            }

            <Typography variant="h6" color="primary">
              Request Access Emails
            </Typography>
            <div className="mb-3">
              If you have skinbetter science products on your store or other restricted brands that require access
              approval, Access Request Notifications will be Blind Copied (BCC) to the Receiving Email Address(es).
            </div>
            <Grid item xs={12} className="flex mt-3 mb-6">
              <Grid item xs={7}>
                <TextField
                  value={values.requestAccessEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  className="pr-5"
                  name="requestAccessEmail"
                  label="staffmember1@yourdomain.com,staffmember2@yourdomain.com"
                  variant="outlined"
                  InputLabelProps={{ style: { fontStyle: 'italic' } }}
                  error={touched.requestAccessEmail && Boolean(errors.requestAccessEmail)}
                  helperText={touched.requestAccessEmail && errors.requestAccessEmail}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={values.requestAccessEmail === requestAccessEmail || Boolean(errors.requestAccessEmail)}
                  onClick={() => {
                    handleUpdateEmails('request_access/', values.requestAccessEmail);
                  }}
                >
                  Update
                </Button>
              </Grid>
            </Grid>

            <Typography variant="h6" color="primary">
              On Hold Emails
            </Typography>
            <div className="mb-3">
              If your store&apos;s MaxMind Fraud Detection service assigns a high riskScore to an order, you
              will receive an On Hold Notification email that alerts you to review the order before fulfilling it.
            </div>
            <Grid item xs={12} className="flex mt-3 mb-6">
              <Grid item xs={7}>
                <TextField
                  value={values.onHoldEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  className="pr-5"
                  name="onHoldEmail"
                  label="staffmember1@yourdomain.com"
                  variant="outlined"
                  InputLabelProps={{ style: { fontStyle: 'italic' } }}
                  error={touched.onHoldEmail && Boolean(errors.onHoldEmail)}
                  helperText={touched.onHoldEmail && errors.onHoldEmail}
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={values.onHoldEmail === onHoldEmail || Boolean(errors.onHoldEmail)}
                  onClick={() => {
                    handleUpdateEmails('dermpro_maxmind', values.onHoldEmail);
                  }}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          </form>)}
      </Formik>
    </div>
  );
};

export default StoreConfigurationsReceivingEmails;

